/** admin/import — HOOKS. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { importApi } from './api';
import { adminImportKeys } from './keys';
import type { ImportExecuteResponse, ImportJobDetail, ImportPlanResponse, ImportSummary, PaginatedJobs } from './types';

export function useImportJobs(page = 1, limit = 20) {
  return useQuery<PaginatedJobs>({
    queryKey: adminImportKeys.jobs(page, limit),
    queryFn: () => importApi.listJobs(page, limit),
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
  });
}

const TERMINAL_STATUSES = new Set(['completed', 'failed', 'partial', 'aborted']);

export function useImportJob(id: string | null) {
  return useQuery<ImportJobDetail>({
    queryKey: adminImportKeys.job(id ?? 'none'),
    queryFn: () => importApi.getJob(id!),
    enabled: !!id,
    refetchInterval: (q) => {
      const data = q.state.data;
      if (!data) return 2_000;
      return TERMINAL_STATUSES.has(data.status) ? false : 2_000;
    },
    refetchIntervalInBackground: false,
  });
}

export function useImportPreview() {
  return useMutation<ImportPlanResponse, Error, object>({
    mutationFn: (file: object) => importApi.preview(file),
  });
}

export function useImportExecute() {
  const qc = useQueryClient();
  return useMutation<ImportExecuteResponse, Error, { file: object; expectedSummary?: ImportSummary }>({
    mutationFn: (v) => importApi.execute(v.file, v.expectedSummary),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminImportKeys.all() }),
  });
}
